import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  ListGroup,
  Spinner,
  Alert,
} from "react-bootstrap";
import { FiShield, FiPlus, FiTrash2, FiInfo } from "react-icons/fi";
import { getHost, defaultClientId } from "../utils/utils";

const getToken = () => localStorage.getItem("jwt");

const BlacklistModal = ({ show, onHide, monitor, clients }) => {
  const [selectedClient, setSelectedClient] = useState("");
  const [blacklist, setBlacklist] = useState([]);
  const [recentPids, setRecentPids] = useState([]);
  const [newPid, setNewPid] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingExamples, setLoadingExamples] = useState(false);
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState("");

  const fetchBlacklist = useCallback(async () => {
    setLoading(true);
    setMessage("");

    try {
      const res = await fetch(
        `${getHost()}/api/blacklist?monitor_id=${monitor._id}&client_id=${selectedClient}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );

      const data = await res.json();
      setBlacklist(data.blacklist || []);
    } catch (error) {
      setMessage("Failed to load blacklist");
    } finally {
      setLoading(false);
    }
  }, [monitor, selectedClient]);

  const fetchRecentPids = useCallback(async () => {
    setLoadingExamples(true);

    try {
      const res = await fetch(
        `${getHost()}/api/products/recent?monitor_id=${monitor._id}&limit=4`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );

      const data = await res.json();
      setRecentPids(data.products || []);
    } catch (error) {
      console.error("Failed to load PID examples");
    } finally {
      setLoadingExamples(false);
    }
  }, [monitor]);

  // Reset when modal opens/closes
  useEffect(() => {
    if (show) {
      setSelectedClient(defaultClientId(clients));
      setBlacklist([]);
      setRecentPids([]);
      setMessage("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Fetch blacklist and examples when client is selected
  useEffect(() => {
    if (!monitor || !selectedClient) return;

    fetchBlacklist();
    fetchRecentPids();
  }, [monitor, selectedClient, fetchBlacklist, fetchRecentPids]);

  const handleAdd = async (e) => {
    e.preventDefault();

    if (!newPid.trim()) return;

    setAdding(true);
    setMessage("");

    try {
      const res = await fetch(`${getHost()}/api/blacklist/add`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          monitor_id: monitor._id,
          client_id: selectedClient,
          pid: newPid.trim(),
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error);

      setBlacklist(data.blacklist);
      setNewPid("");
      setMessage("✅ PID added to blacklist!");

      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      setMessage(`❌ ${error.message || "Failed to add PID"}`);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (pid) => {
    if (!window.confirm(`Remove ${pid} from blacklist?`)) return;

    setMessage("");

    try {
      const res = await fetch(`${getHost()}/api/blacklist/remove`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          monitor_id: monitor._id,
          client_id: selectedClient,
          pid,
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error);

      setBlacklist(data.blacklist);
      setMessage("✅ PID removed from blacklist!");

      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      setMessage(`❌ ${error.message || "Failed to remove PID"}`);
    }
  };

  if (!monitor) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="md">
      <Modal.Header closeButton className="border-secondary">
        <Modal.Title className="text-main d-flex align-items-center gap-2">
          <FiShield className="fs-22" />
          <div className="fs-18 fw-600">
            PID Blacklist
            <div className="fs-14 text-white-45 mt-1">{monitor.name}</div>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="text-white modal-white-bg">
        {message && (
          <Alert
            variant={message.includes("✅") ? "success" : "danger"}
            dismissible
            onClose={() => setMessage("")}
            className="mb-3"
          >
            {message}
          </Alert>
        )}

        {/* Client Selector */}
        <Form.Group className="mb-3">
          <Form.Label className="text-main">Select Group/Client</Form.Label>
          <Form.Select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="form-input-field rounded-3 text-main custom-select-white"
          >
            <option value="">Select a group...</option>
            {clients.map((cl) => (
              <option key={cl._id} value={cl._id}>
                {cl.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {selectedClient && (
          <>
            {/* PID Format Examples */}
            {recentPids.length > 0 && (
              <Alert
                className="mb-3"
                style={{
                  backgroundColor: "rgba(255, 87, 123, 0.1)",
                  border: "1px solid rgba(255, 87, 123, 0.3)",
                  color: "#ffffff",
                }}
              >
                <div className="d-flex align-items-start gap-2">
                  <FiInfo
                    className="mt-1 flex-shrink-0"
                    style={{ color: "#FF577B" }}
                  />
                  <div className="w-100">
                    <strong className="text-white">PID Format Examples:</strong>
                    <div className="mt-2">
                      {loadingExamples ? (
                        <Spinner size="sm" />
                      ) : (
                        recentPids.map((product, idx) => (
                          <code
                            key={idx}
                            className="me-2 mb-1 d-inline-block"
                            style={{
                              backgroundColor: "rgba(255, 255, 255, 0.1)",
                              color: "#FF577B",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              border: "1px solid rgba(255, 87, 123, 0.3)",
                            }}
                          >
                            {product.pid}
                          </code>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </Alert>
            )}

            {/* Add PID Form */}
            <Form onSubmit={handleAdd} className="mb-4">
              <Form.Group>
                <Form.Label className="text-main">
                  Add PID to Blacklist
                </Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    value={newPid}
                    onChange={(e) => setNewPid(e.target.value)}
                    placeholder="Enter PID (e.g., DV3854-200)"
                    className="form-input-field"
                    disabled={adding}
                  />
                  <Button
                    type="submit"
                    variant="danger"
                    disabled={adding || !newPid.trim()}
                    className="d-flex align-items-center gap-2"
                    style={{
                      minWidth: "80px",
                    }}
                  >
                    {adding ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <FiPlus /> Add
                      </>
                    )}
                  </Button>
                </div>
              </Form.Group>
            </Form>

            {/* Blacklist Display */}
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="text-main mb-0">
                  Blacklisted PIDs ({blacklist.length})
                </Form.Label>
              </div>

              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="light" size="sm" />
                </div>
              ) : blacklist.length === 0 ? (
                <div className="text-center py-4 text-white-65">
                  No PIDs blacklisted yet
                </div>
              ) : (
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  <ListGroup variant="flush">
                    {blacklist.map((pid, index) => (
                      <ListGroup.Item
                        key={index}
                        className="d-flex justify-content-between align-items-center bg-transparent border-secondary text-white px-0 py-2"
                      >
                        <code className="text-main">{pid}</code>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemove(pid)}
                        >
                          <FiTrash2 />
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="border-secondary">
        <Button
          variant="secondary"
          onClick={onHide}
          className="rounded-3 py-2 px-3 fw-600 fs-12"
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BlacklistModal;
